/* eslint-disable max-lines-per-function */
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { disableAdsIframeSendHashAnalytics } from 'reactApp/appHelpers/featuresHelpers/features/overquotaCommunications';
import type { Product } from 'reactApp/types/Billing';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import type { OnResizeCallback } from 'reactApp/ui/IframePages/BuyDisableAds/BuyDisableAds.types';
import { useDisableAdsAnalytics } from 'reactApp/ui/IframePages/BuyDisableAds/hooks/useDisableAdsAnalytics';
import { useDisableAdsTariffs } from 'reactApp/ui/IframePages/BuyDisableAds/hooks/useDisableAdsTariffs';
import { useDisableAdsTitle } from 'reactApp/ui/IframePages/BuyDisableAds/hooks/useDisableAdsTitle';
import { ETabsName } from 'reactApp/ui/TariffSectionNew/TariffSection.constants';
import { TariffsModalContent } from 'reactApp/ui/TariffsModal/TariffsModalContent';
import { sendTariffsHash } from 'reactApp/utils/analytics/sendTariffsHash';
import { sendDwh } from 'reactApp/utils/ga';
import { noopVoid } from 'reactApp/utils/helpers';
import useResizeObserver from 'use-resize-observer/polyfilled';

import styles from './BuyDisableAds.css';

export interface BuyDisableAdsProps {
    title?: string;
    titleFromQuery?: string;
    tariffsNumber?: string;
    activeTab?: string;
    activeTabFromQuery?: string;
    onClose?: () => void;
    onBuyClick: (product: Product) => void;
    onResize?: OnResizeCallback;
    analyticsFromQuery?: string;
    usePersonal?: boolean;
    isIframe?: boolean;
    name?: string;
    paySource?: string;
    actionShow?: string;
    actionSwitch?: string;
    actionClick?: string;
    modal?: string;
}

const eventCategory = 'tariffs-quota';

export const BuyDisableAds = memo(
    ({
        title,
        titleFromQuery,
        activeTab,
        activeTabFromQuery,
        tariffsNumber,
        onClose = noopVoid,
        onBuyClick,
        onResize,
        paySource,
        name,
        actionShow = 'modal-noads-show',
        actionSwitch = 'modal-noads-switch',
        actionClick = 'modal-noads-click',
        analyticsFromQuery,
        usePersonal,
        modal = 'none',
        isIframe = false,
    }: BuyDisableAdsProps) => {
        const titleByQuery = useDisableAdsTitle(titleFromQuery);
        const { tariffs, tariffIds, activeTabPersonal } = useDisableAdsTariffs(tariffsNumber, usePersonal);

        let _activeTab = activeTab;

        if (isIframe) {
            _activeTab = usePersonal && activeTabPersonal ? activeTabPersonal.charAt(0) : activeTabFromQuery;
        }

        const [tabActive, setTabActive] = useState<ETabsName>(_activeTab === 'm' ? ETabsName.month : ETabsName.year);

        const {
            name: _name,
            paySource: _paySource,
            actionShow: _actionShow,
            actionClick: _actionClick,
            actionSwitch: _actionSwitch,
        } = useDisableAdsAnalytics(analyticsFromQuery, {
            name,
            paySource,
            actionShow,
            actionClick,
            actionSwitch,
        });

        const analyticsTariffList = [...(tariffIds?.year && tariffIds.year), ...(tariffIds?.month && tariffIds.month)];

        const onResizeWrapper = useCallback(
            ({ height = 0 }: { height?: number }) => {
                onResize?.({ height });
            },
            [onResize]
        );
        const { ref } = useResizeObserver<HTMLDivElement>({ onResize: onResizeWrapper });

        const isFirstEffectCalled = useRef(false);

        useEffect(() => {
            if (!tariffs.length) {
                return;
            }

            sendDwh({
                dwh_enabled: true,
                dwh_enabled_fields: [['name', 'selectedPeriod', 'paySource']],
                eventCategory,
                action: _actionShow,
                dwhData: {
                    name: _name || 'none',
                    selectedPeriod: _activeTab,
                    paySource: _paySource,
                    tariffList: tariffsNumber,
                    tariff_list: analyticsTariffList,
                },
            });

            if (!isIframe || disableAdsIframeSendHashAnalytics) {
                sendTariffsHash({ tariffs, paySource: _paySource, tariffsNumber });
            }
        }, [tariffs.length]);

        useEffect(() => {
            if (!isFirstEffectCalled.current) {
                emitAnalyticEvent(AnalyticEventNames.BUY_DISABLE_ADS_MODAL_SHOWN, { selectedPeriod: tabActive, modal });

                isFirstEffectCalled.current = true;
            } else {
                emitAnalyticEvent(AnalyticEventNames.BUY_DISABLE_ADS_MODAL_PERIOD_CHANGE, { selectedPeriod: tabActive });
                sendDwh({
                    dwh_enabled: true,
                    eventCategory,
                    action: _actionSwitch,
                    dwhData: {
                        selectedPeriod: _activeTab,
                        name: _name,
                        paySource: _paySource,
                    },
                });
            }
        }, [tabActive]);

        const onBuyWithRadar = (product: Product) => {
            emitAnalyticEvent(AnalyticEventNames.BUY_DISABLE_ADS_MODAL_TARIFF_CLICK, { product, modal });
            sendDwh({
                eventCategory,
                action: _actionClick,
                dwh_enabled: true,
                dwh_enabled_fields: [['name', 'selectedPeriod', 'paySource']],
                dwhData: {
                    name: _name,
                    selectedPeriod: _activeTab,
                    paySource: _paySource,
                    tariff: product.id,
                    tariff_list: analyticsTariffList,
                },
            });
            onBuyClick(product);
        };

        const content = (
            <TariffsModalContent
                isIframe={isIframe}
                tariffs={tariffs}
                yearTariffsCount={tariffIds.year.length}
                monthTariffsCount={tariffIds.month.length}
                activeTab={tabActive}
                setActiveTab={setTabActive}
                onBuyClick={onBuyWithRadar}
                title={title || titleByQuery || ''}
            />
        );

        if (isIframe) {
            return (
                <div ref={ref} className={styles.contentRefWrapper}>
                    {content}
                </div>
            );
        }

        return (
            <Dialog id="disable-ads-screen" open mod="frame" closeOnDimmerClick={false} onCancel={onClose}>
                {content}
            </Dialog>
        );
    }
);

BuyDisableAds.displayName = 'BuyDisableAds';
